import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, BookOpen, Building2, ChevronRight, GraduationCap, Globe, Bookmark, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [stats, setStats] = useState({ totalJobs: 0, countries: 0, institutions: 0, fields: 0 });

  useEffect(() => {
    // Seed database and fetch featured jobs
    const initData = async () => {
      try {
        // Seed first
        await fetch(`${API}/seed`, { method: "POST" });
        
        // Fetch jobs
        const response = await fetch(`${API}/jobs?limit=6`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedJobs(data.jobs || []);
          setStats({
            totalJobs: data.total || 0,
            countries: 8,
            institutions: 10,
            fields: 9
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    initData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/jobs?search=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1708548172199-72f7796d4206?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHx1bml2ZXJzaXR5JTIwbGlicmFyeSUyMGRhcmslMjBhY2FkZW1pYXxlbnwwfHx8fDE3NzMwODczNzd8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-96 h-96 bg-[#800020]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <span className="inline-block px-4 py-2 bg-[#800020]/20 border border-[#800020]/30 rounded-sm text-[#D4AF37] text-sm font-semibold uppercase tracking-widest mb-6">
              Your Next Academic Chapter
            </span>
            
            <h1 className="font-serif text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] text-white mb-6">
              Discover <span className="gold-text">Postdoctoral</span> Positions Worldwide
            </h1>
            
            <p className="font-sans text-lg md:text-xl text-gray-400 leading-relaxed mb-10 max-w-2xl">
              Connect with elite research institutions across the globe. Find your next position at universities, research labs, and institutes that are shaping the future of science.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 mb-12">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search by field, institution, or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 text-lg rounded-sm w-full search-input"
                  data-testid="hero-search-input"
                />
              </div>
              <Button 
                type="submit"
                className="h-14 px-8 bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold uppercase tracking-wide text-sm rounded-sm btn-gold"
                data-testid="hero-search-btn"
              >
                Find Positions
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </form>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: BookOpen, label: "Open Positions", value: stats.totalJobs + "+" },
                { icon: Globe, label: "Countries", value: stats.countries + "+" },
                { icon: Building2, label: "Institutions", value: stats.institutions + "+" },
                { icon: GraduationCap, label: "Research Fields", value: stats.fields + "+" },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-sm bg-[#800020]/20 flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Jobs Section */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h3 className="font-sans text-sm font-bold tracking-widest uppercase text-[#D4AF37] mb-2">
                Featured Opportunities
              </h3>
              <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-white">
                Latest Positions
              </h2>
            </div>
            <Link to="/jobs">
              <Button variant="outline" className="hidden sm:flex border-white/20 text-white hover:bg-white/5 rounded-sm" data-testid="view-all-jobs-btn">
                View All Jobs
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="bento-grid">
            {featuredJobs.map((job, index) => (
              <motion.div
                key={job.job_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`job-card group relative overflow-hidden rounded-sm border border-white/10 bg-[#0A0A0A] p-6 ${index === 0 ? 'bento-featured' : ''}`}
              >
                <Link to={`/jobs/${job.job_id}`} className="block" data-testid={`job-card-${job.job_id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <span className="inline-block px-3 py-1 bg-[#800020]/20 text-[#D4AF37] text-xs font-semibold uppercase tracking-wide rounded-sm">
                      {job.field}
                    </span>
                    <Bookmark className="w-5 h-5 text-gray-500 group-hover:text-[#D4AF37] transition-colors" />
                  </div>
                  
                  <h3 className="font-serif text-xl font-semibold text-white mb-2 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                    {job.title}
                  </h3>
                  
                  <p className="text-gray-400 mb-4 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {job.institution}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {job.location}, {job.country}
                    </span>
                  </div>
                  
                  {job.deadline && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <p className="text-xs text-gray-500">
                        Deadline: <span className="text-[#D4AF37]">{new Date(job.deadline).toLocaleDateString()}</span>
                      </p>
                    </div>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link to="/jobs">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-sm w-full" data-testid="view-all-jobs-mobile-btn">
                View All Jobs
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="font-sans text-sm font-bold tracking-widest uppercase text-[#D4AF37] mb-2">
              Why Choose Us
            </h3>
            <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-white">
              Your Academic Career Hub
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Globe,
                title: "Global Reach",
                description: "Access postdoc positions from top universities and research institutions across every continent."
              },
              {
                icon: Bookmark,
                title: "Track Applications",
                description: "Save positions, track your applications, and manage your academic job search in one place."
              },
              {
                icon: Users,
                title: "Curated Listings",
                description: "Aggregated from leading academic job boards including Nature Careers, Academic Jobs Online, and more."
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="rounded-sm bg-gradient-to-br from-white/5 to-transparent p-8 border border-white/5"
              >
                <div className="w-12 h-12 rounded-sm bg-[#800020]/20 flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-[#800020]/5" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight text-white mb-6">
            Ready to Start Your Search?
          </h2>
          <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
            Create an account to save positions, track applications, and get notified about new opportunities in your field.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Link to="/dashboard">
                <Button className="h-14 px-8 bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold uppercase tracking-wide text-sm rounded-sm btn-gold" data-testid="go-to-dashboard-btn">
                  Go to Dashboard
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/register">
                  <Button className="h-14 px-8 bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold uppercase tracking-wide text-sm rounded-sm btn-gold" data-testid="cta-register-btn">
                    Create Free Account
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/jobs">
                  <Button variant="outline" className="h-14 px-8 border-white/20 text-white hover:bg-white/5 uppercase tracking-wide text-sm rounded-sm" data-testid="cta-browse-btn">
                    Browse Positions
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-[#D4AF37]" />
              <span className="font-serif text-xl text-white">PostDoc Finder</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 PostDoc Finder. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
